import React, { useEffect, useState } from 'react'
import {
  CBadge,
  CButton,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  CSmartTable,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilReload } from '@coreui/icons'
import CommonService from 'src/service/CommonService'
import {
  formatBuild,
  formatDeviceDetails,
  formatPlatform,
  formatUtcDateTime,
} from 'src/utils/appDevice'

// Session start and device are filled in per page after the query, so the API cannot sort
// by them and neither can the table.
const columns = [
  { label: 'Member ID', key: 'id' },
  { label: 'Member Name', key: 'full_name' },
  { label: 'Email', key: 'email' },
  { label: 'Phone', key: 'phone' },
  { label: 'Session Start', key: 'login_at', filter: false, sorter: false },
  { label: 'Last Activity', key: 'last_activity_at', filter: false },
  { label: 'Device', key: 'device_type', sorter: false },
  { label: 'Status', key: 'online_status', filter: false, sorter: false },
]

// Who is online in one game right now — the drill-down behind the Online column. Presence
// comes from the mobile app's app_events (an event in the last 5 minutes). Mounted only while
// a game is picked, so paging/filters start clean on every game.
const GameOnlineMembers = ({ gameId, gameName, onClose }) => {
  const [loading, setLoading] = useState(false)
  const [members, setMembers] = useState()
  const [activePage, setActivePage] = useState(1)
  const [columnFilter, setColumnFilter] = useState([])
  const [columnSorter, setColumnSorter] = useState(null)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  // Online is live data, so let the admin re-pull it without reopening the modal.
  const [refreshKey, setRefreshKey] = useState(0)

  useEffect(() => {
    if (!gameId) return
    setLoading(true)

    const offset = itemsPerPage * activePage - itemsPerPage
    let params = new URLSearchParams()

    Object.keys(columnFilter).forEach((key) => {
      params.append(key, columnFilter[key])
    })

    columnSorter &&
      columnSorter.column !== undefined &&
      params.append('sort', `${columnSorter.column}%${columnSorter.state}`)

    CommonService.gameOnlineMembers(gameId, offset, itemsPerPage, activePage, params)
      .then((result) => {
        setMembers(result.data)
        setLoading(false)
      })
      .catch(() => {
        setLoading(false)
      })
  }, [gameId, activePage, columnFilter, columnSorter, itemsPerPage, refreshKey])

  return (
    <CModal visible size="xl" scrollable onClose={onClose}>
      <CModalHeader>
        <div>
          <CModalTitle>Online Members</CModalTitle>
          <p className="ga-online-modal-subtitle">
            {gameName} — Count: {members?.total ?? 0}
          </p>
        </div>
      </CModalHeader>

      <CModalBody>
        <div className="ge-datatable ge-datatable--no-select ge-datatable--static ga-table">
          <CSmartTable
            columns={columns}
            columnFilter={{ external: true }}
            columnSorter={{ external: true }}
            noItemsLabel="No members online in this game"
            scopedColumns={{
              login_at: (item) => <td>{formatUtcDateTime(item.login_at)}</td>,
              last_activity_at: (item) => <td>{formatUtcDateTime(item.last_activity_at)}</td>,
              device_type: (item) => {
                const platform = formatPlatform(item.device_type)
                if (!platform) return <td>-</td>
                const build = formatBuild(item)
                const details = formatDeviceDetails(item)
                return (
                  <td title={details || undefined}>
                    <div className="fw-semibold">{platform}</div>
                    {build && <div className="text-body-secondary small">{build}</div>}
                  </td>
                )
              },
              online_status: () => (
                <td>
                  <CBadge color="success">Online</CBadge>
                </td>
              ),
            }}
            items={members?.data}
            itemsPerPage={itemsPerPage}
            itemsPerPageSelect
            loading={loading}
            pagination={{ external: true }}
            paginationProps={{
              activePage: activePage,
              pages: Math.ceil(members?.total / itemsPerPage) || 1,
            }}
            tableProps={{ hover: true, responsive: true, className: 'align-middle mb-0' }}
            onActivePageChange={(page) => setActivePage(page)}
            onColumnFilterChange={(filter) => {
              setActivePage(1)
              setColumnFilter(filter)
            }}
            onItemsPerPageChange={(perPage) => {
              setActivePage(1)
              setItemsPerPage(perPage)
            }}
            onSorterChange={(sorter) => setColumnSorter(sorter)}
          />
        </div>
      </CModalBody>

      <CModalFooter>
        <CButton
          color="secondary"
          variant="outline"
          onClick={() => setRefreshKey((current) => current + 1)}
          title="Refresh"
        >
          <CIcon icon={cilReload} className="me-1" />
          Refresh
        </CButton>
        <CButton color="secondary" onClick={onClose}>
          Close
        </CButton>
      </CModalFooter>
    </CModal>
  )
}

export default GameOnlineMembers
